/**
 * Open one email message in its own window.
 *
 * Given a mailbox secId and a Gmail message id, fetches the full message and
 * shows it — subject, sender, attachments, and the HTML body inside a
 * sandboxed iframe (no allow-scripts, so the email's own markup and JS can
 * neither escape into the dashboard nor run).
 *
 * Shared so every place that surfaces a message — the sales inbox mail search,
 * the worklist timeline — opens the exact same viewer.
 */
define('salesInboxMailMessagePopup',[
  'module',
  'app',
  'jquery',
  'underscore',
  'settings'
], function (Module, App, $, _, Settings) {
  'use strict';

  function escapeHtml(s) {
    var str = (s === null || s === undefined) ? '' : String(s);
    return str
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function fmtBytes(n) {
    n = Number(n) || 0;
    if (!n) { return ''; }
    if (n < 1024) { return n + ' B'; }
    if (n < 1048576) { return Math.round(n / 1024) + ' KB'; }
    return (n / 1048576).toFixed(1) + ' MB';
  }

  function attachmentsHtml(full, eaSec, bodyHtml) {
    // Only hide attachments that are TRULY embedded in the body (an inline
    // image referenced by its cid, e.g. a signature logo). A real attachment
    // is shown even when the sender flagged it Content-Disposition: inline.
    function isEmbedded(a) {
      if (!a.inline || !a.contentId) { return false; }
      var cid = String(a.contentId).replace(/[<>]/g, '');
      return cid && bodyHtml.indexOf(cid) !== -1;
    }
    return _.map((full.attachments || []), function (a) {
      if (!a || !a.fileName || isEmbedded(a)) { return ''; }
      var url = Settings.url('compuzz2', 'mails/fetch/' + eaSec + '/' + (full.id || '') +
        '/download/' + encodeURIComponent(a.fileName));
      var sz = fmtBytes(a.size);
      return '<a href="' + url + '" target="_blank" rel="noopener" title="Download" ' +
        'style="display:inline-flex;align-items:center;gap:5px;max-width:240px;padding:4px 9px;' +
        'border:1px solid #d6dae0;border-radius:7px;background:#f7f8fa;color:#2563eb;' +
        'text-decoration:none;font-size:12px;">' +
        '<span>📎</span><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' +
        escapeHtml(a.fileName) + '</span>' +
        (sz ? '<span style="color:#8a8f98;flex:0 0 auto;">' + sz + '</span>' : '') +
        '</a>';
    }).join('');
  }

  var API = {

    /**
     * Show an already-fetched message (the shape `mails:get-mail-url` returns).
     */
    show: function (Windows, Marionette, eaSec, full) {
      var subject = full.subject || '(no subject)';
      var fromAddr = (full.from && full.from[0]) || {};
      var fromTxt = fromAddr.personal ?
        (fromAddr.personal + ' <' + (fromAddr.address || '') + '>') :
        (fromAddr.address || '');
      var date = full.sentDate ? new Date(full.sentDate).toLocaleString('fr-BE') : '';
      var bodyHtml = full.content || full.contentText || '<em>No content.</em>';
      var attsHtml = attachmentsHtml(full, eaSec, bodyHtml);

      var MsgView = Marionette.ItemView.extend({
        className: 'mail-search-message',
        template: _.template(
          '<div class="msm-head" style="padding:0 2px 10px;border-bottom:1px solid #e2e5e9;margin-bottom:10px;flex:0 0 auto;">' +
          '  <div style="font-size:15px;font-weight:700;color:#1a1a1a;"><%= subject %></div>' +
          '  <div style="font-size:12px;color:#5f6368;margin-top:4px;"><%= from %><% if (date) { %> · <%= date %><% } %></div>' +
          '</div>' +
          '<% if (atts) { %><div class="msm-atts" style="flex:0 0 auto;display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px;"><%= atts %></div><% } %>' +
          '<iframe class="msm-frame" sandbox="allow-same-origin" title="Email content" ' +
          'style="flex:1 1 auto;width:100%;border:0;min-height:420px;"></iframe>'
        ),
        templateHelpers: function () {
          return {
            subject: escapeHtml(subject),
            from: escapeHtml(fromTxt),
            date: escapeHtml(date),
            atts: attsHtml
          };
        },
        onRender: function () {
          this.$el.css({ display: 'flex', 'flex-direction': 'column', height: '100%' });
          var iframe = this.$('.msm-frame')[0];
          // Sandbox WITHOUT allow-scripts → the email's JS can't run (XSS-safe).
          if (iframe) { iframe.srcdoc = bodyHtml; }
        }
      });

      Windows.open({
        view: function () { return new MsgView(); },
        id: 'sales-inbox-mailmsg:' + (full.id || ''),
        title: subject,
        modal: true,
        width: '78%',
        height: '86%'
      });
    },

    /**
     * Fetch the message from its mailbox, then show it. Silently does nothing
     * when either id is missing or the fetch fails — a message we can't load is
     * not worth an empty window.
     */
    open: function (eaSec, msgId) {
      if (!eaSec || !msgId) { return; }
      require(['entities/mails', 'windows', 'marionette'], function (MailsEntities, Windows, Marionette) {
        $.ajax({ url: App.request('mails:get-mail-url', eaSec, 'ALL MAILS', msgId) })
          .done(function (full) {
            if (!full) { return; }
            API.show(Windows, Marionette, eaSec, full);
          });
      });
    }
  };

  Module.exports = API;
});

